import {
  Button,
  Coin,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Stack,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';

/**
 * What became of a queued export, reported the way Ember's async-export modal
 * does: a status coin, a sentence, and a way on to the exports page — or, when
 * the request was refused, a way to try again.
 *
 * No heading, matching Ember, which passes the modal no titles for this flow;
 * the dialog is named for assistive tech instead.
 */

const QUEUED_ARIA_LABEL = 'Export to CSV';
const FAILED_MESSAGE = 'There was a problem processing your export. Try again.';

/**
 * What became of the export. Only a refused one can be retried, so only a
 * refused one carries the retry: a caller that reports refusals some other
 * way cannot be asked for a handler the modal would never call.
 */
export type ExportResultModalData = {
  /** The exports page the finished file will appear on. */
  exportsHref: string;
} & (
  | { outcome: 'queued' }
  | {
      outcome: 'failed';
      /**
       * Runs the export again. Owned by the page: a managed modal mounts
       * outside its providers, so it cannot reach the mutation itself.
       */
      onRetry: () => void;
    }
);

export const ExportResultModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<ExportResultModalData>) => {
  const { outcome, exportsHref } = data;
  const isQueued = outcome === 'queued';

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      shouldShowCloseButton={false}
      aria-label={QUEUED_ARIA_LABEL}
      data-test-export-result-modal={outcome}
    >
      <ModalBody>
        <Stack alignInline="center" gap="200" p="300">
          <Coin colorVariant={isQueued ? 'green' : 'red'} size="xl">
            <Icon name={isQueued ? 'check-circle' : 'critical'} />
          </Coin>

          <Paragraph textAlign="center" data-test-export-summary={outcome}>
            {isQueued ? (
              <>
                Your export is currently processing. When it is ready, you will receive an email, and it will be
                available for download on the{' '}
                <Link href={exportsHref} isUnderlined>
                  exports page
                </Link>
                .
              </>
            ) : (
              FAILED_MESSAGE
            )}
          </Paragraph>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()} data-test-export-result-button="close">
          Close
        </Button>
        {isQueued ? (
          <Button
            variant="primary"
            href={exportsHref}
            onClick={() => onClose()}
            data-test-export-result-button="exports"
          >
            Go to Exports
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              onConfirm();
              if (data.outcome === 'failed') {
                data.onRetry();
              }
            }}
            data-test-export-result-button="retry"
          >
            Retry
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
